import React from 'react';

import { useScope } from '../../context/PermissionContext';
import {
  outOfDepartmentCopy,
  NO_ACCESS_ADMISSION_COPY,
  NO_ACCESS_ADMIT_COPY,
  NO_ACCESS_ABHA_COPY,
  ACCESS_CHANGED_COPY,
} from '../../constants/admission';

/**
 * NoAccessPanel (admission-access S1 / S7-AC9, mockup screen 3) — the IPD page's
 * amber panel pattern, ONE component for every "you can't see this" surface:
 *
 *   ADMISSION / ADMIT  — a route the member holds no slug for (`NoAccessGate`,
 *                        which owns the page chrome, the router and the session)
 *   CHANGED            — the slug disappeared while the page was open
 *   OUT_OF_DEPARTMENT  — one admission another department owns (desk deep link
 *                        and every workspace panel the server refuses)
 *
 * Deliberately NOT `pages/AccessDenied.js`: that page clears the session; this
 * one keeps the member signed in with the app header intact. Presentational —
 * only `useScope` (role → out-of-department wording), so it can sit inside any
 * panel without a Router.
 */
export const NO_ACCESS_VARIANT = {
  ADMISSION: 'ADMISSION',
  ADMIT: 'ADMIT',
  ABHA: 'ABHA',
  CHANGED: 'CHANGED',
  OUT_OF_DEPARTMENT: 'OUT_OF_DEPARTMENT',
};

const BACK_TO_DASHBOARD = '← Back to dashboard';
const BACK_TO_LIST = '← Back to my list';

const VARIANTS = {
  [NO_ACCESS_VARIANT.ADMISSION]: {
    title: 'Manage Admission', message: NO_ACCESS_ADMISSION_COPY, backLabel: BACK_TO_DASHBOARD, testId: 'no-access',
  },
  [NO_ACCESS_VARIANT.ADMIT]: {
    title: 'Admit Patient', message: NO_ACCESS_ADMIT_COPY, backLabel: BACK_TO_DASHBOARD, testId: 'no-access',
  },
  [NO_ACCESS_VARIANT.ABHA]: {
    title: 'ABHA', message: NO_ACCESS_ABHA_COPY, backLabel: BACK_TO_DASHBOARD, testId: 'no-access',
  },
  [NO_ACCESS_VARIANT.CHANGED]: {
    title: 'Manage Admission', message: ACCESS_CHANGED_COPY, backLabel: BACK_TO_DASHBOARD, testId: 'no-access',
  },
  [NO_ACCESS_VARIANT.OUT_OF_DEPARTMENT]: {
    title: 'Admission not available',
    message: null, // role-aware — rendered by <OutOfDepartmentText />
    backLabel: BACK_TO_LIST,
    testId: 'out-of-scope',
  },
};

/** Role-aware out-of-department sentence (only mounted when that refusal shows). */
export function OutOfDepartmentText() {
  const { role } = useScope();
  return outOfDepartmentCopy(role);
}

/** `signedInAs` / `onBack` are optional — no line / no button without them. */
export default function NoAccessPanel({ variant, signedInAs, onBack }) {
  const v = VARIANTS[variant];
  return (
    <div
      role="alert"
      data-testid={v.testId}
      className="rounded-xl border border-amber-400 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 p-5 text-amber-900 dark:text-amber-100"
    >
      <h2 className="mb-1 text-lg font-semibold">{v.title}</h2>
      <p className="text-sm">{variant === NO_ACCESS_VARIANT.OUT_OF_DEPARTMENT ? <OutOfDepartmentText /> : v.message}</p>
      {signedInAs && <div className="mt-3 text-xs opacity-90" data-testid="no-access-user">Signed in as {signedInAs}.</div>}
      {typeof onBack === 'function' && (
        <div className="mt-3.5">
          <button
            type="button"
            onClick={onBack}
            data-testid="no-access-back"
            className="px-3.5 py-1.5 rounded-lg text-[13px] font-semibold bg-teal-600 hover:bg-teal-700 text-white"
          >
            {v.backLabel}
          </button>
        </div>
      )}
    </div>
  );
}
